<template>
  <v-container
    :key="$route.params.id || 'new'"
    grid-list-xl
    fluid
  >
    <LoadingState
      v-if="loading"
      :loading="loading"
      color="primary"
    />
    <v-layout
      v-if="!loading"
      row
      wrap
    >
      <v-flex
        xs12
      >
        <v-alert
          :value="order.errors.length"
          type="error"
          icon="new_releases"
          dismissible
        >
          <div v-html="order.errorsText()"/>
        </v-alert>
      </v-flex>
      <v-flex
        xs12
        md8
      >
        <order-draft-items
          :order="order"
          @add-item="onOpenProductSearch"
          @item-removed="onRemoveProduct"
          @item-updated="updateItem"
        />

        <order-draft-shipping
          :order="order"
          @shipping-selected="changeShippingMethod"
        />

        <order-draft-requested-delivery
          v-model="order.requested_delivery"
          :valid.sync="isRequestDeliveryValid"
          class="mb-4"
        />

        <OrderDraftPayments
          v-if="hasShippingMethod"
          :order="order"
        />
      </v-flex>

      <v-flex
        xs12
        md4
      >
        <order-s-draft-summary
          v-if="order.summary"
          :order="order"
        />
        <order-draft-customer
          :order="order"
          @change-billing-address="changeBillingAddress"
          @change-shipping-address="changeShippingAddress"
          @change-customer="changeCustomer"
        />
        <order-draft-coupon
          :order="order"
          class="mb-4"
          @set-coupon="editCoupon = true"
          @remove-coupon="removeCoupon"
        />
        <order-draft-note :order="order" />
      </v-flex>
    </v-layout>
    <SearchCustomer
      v-if="!order.customer.customer_id && !order.customer.guest_id"
      ref="search_customer"
    />

    <SearchAddress
      ref="search_address"
    />

    <v-dialog
      v-model="searchDialog"
      persistent
      max-width="600px"
      scrollable
    >
      <search-item
        v-if="order"
        :order="order"
        @product-selected="onProductSelected"
        @cancelled="searchDialog = false"
      />
    </v-dialog>

    <v-dialog
      v-model="productDialog"
      persistent
      max-width="1100px"
      scrollable
    >
      <order-draft-product-dialog
        :product="selectedProduct"
        :order="order"
        @cancelled="onProductCancelled"
        @confirmed="onAddProduct"/>
    </v-dialog>

    <v-dialog
      v-model="editCoupon"
      persistent
      max-width="500px"
      scrollable
    >
      <v-card>
        <v-card-title class="headline">
          <span class="pl-2">{{ $t('Coupon Code') }}</span>
          <v-spacer/>
          <v-btn
            icon
            @click="editCoupon = false; selectedCoupon = order.coupon"
          >
            <v-icon large>close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider/>

        <v-card-text>
          <v-select
            v-model="selectedCoupon"
            :items="coupons"
            :disabled="coupons.length === 0"
            :placeholder="couponSelectPlaceholder"
            class="ma-2"
          />
        </v-card-text>

        <v-divider/>

        <v-card-actions class="ma-3">
          <v-spacer/>
          <v-btn
            :disabled="!selectedCoupon"
            color="primary"
            @click="applyCoupon"
          >
            {{ $t('Apply') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import OrderDraftHeader from '../components/OrderDraft/OrderDraftHeader'
import OrderDraftItems from '../components/OrderDraft/OrderDraftItems'
import OrderDraftPayments from '../components/OrderDraft/OrderDraftPayments'
import OrderDraftCustomer from '../components/OrderDraft/OrderDraftCustomer'
import OrderSDraftSummary from '../components/OrderDraft/OrderDraftSummary'
import CustomerAddressWidget from '../../Customers/components/CustomerSingle/CustomerAddressWidget'
import SearchCustomer from '../components/SearchCustomerWidget'
import SearchItem from '../components/SearchItemWidget'
import SearchAddress from '../components/SearchAddressWidget'
import LoadingState from '@/components/LoadingState'
import OrderDraftNote from '../components/OrderDraft/OrderDraftNote'
import OrderDraftProductDialog from '@/modules/Orders/components/OrderDraftProductDialog'
import OrderDraftShipping from '@/modules/Orders/components/OrderDraft/OrderDraftShipping'
import OrderDraftCoupon from '@/modules/Orders/components/OrderDraft/OrderDraftCoupon'
import OrderDraftRequestedDelivery from '@/modules/Orders/components/OrderDraft/OrderDraftRequestedDelivery'

export default {
  name: 'Order',

  components: {
    OrderDraftRequestedDelivery,
    OrderDraftCoupon,
    OrderDraftShipping,
    OrderDraftNote,
    OrderDraftCustomer,
    CustomerAddressWidget,
    OrderDraftItems,
    OrderDraftPayments,
    OrderDraftHeader,
    OrderSDraftSummary,
    SearchCustomer,
    SearchItem,
    SearchAddress,
    LoadingState,
    OrderDraftProductDialog
  },

  async beforeRouteLeave (to, from, next) {
    if (this.order && this.order.isChanged) {
      const answer = await this.$root.$confirm(this.$t('Attention'), this.$t('You have unsaved changes. Are you sure want to leave?'), { color: 'primary' })
      if (answer) {
        this.order.reset()
      }
      return next(answer)
    }
    next()
  },

  actionBar () {
    return {
      title: 'Draft Order',
      actions: [
        {
          color: 'grey',
          text: 'Cancel',
          event: 'cancel'
        },
        {
          color: 'error',
          text: 'Delete',
          event: 'delete',
          hidden: true
        },
        {
          text: 'Checkout',
          event: 'checkout',
          hidden: true
        }
      ]
    }
  },

  data () {
    return {
      order: this.$http.make('cart', {
        customer: {},
        items: [],
        billing_address: {},
        shipping_address: {},
        shipping: {
          method: null
        }
      }),
      redirectAttempts: 0,
      isCreating: false,
      isCheckingOut: false,
      productDialog: false,
      selectedProduct: null,
      searchDialog: false,
      editCoupon: false,
      isRequestDeliveryValid: true,
      selectedCoupon: null,
      coupons: []
    }
  },

  computed: {
    loading () {
      return (!this.order.customer.customer_id && !this.order.items.length) || this.isCreating || this.isCheckingOut
    },

    cancelActionStates () {
      return {
        disabled: !this.order || this.order.inProgress
      }
    },

    checkoutActionStates () {
      return {
        disabled: !this.order || this.order.inProgress || this.order.errors.length || !this.order.items.length || !this.isRequestDeliveryValid,
        loading: this.order && this.order.inProgress,
        hidden: this.order && !this.order.payment
      }
    },

    deleteActionStates () {
      return {
        disabled: !this.order || this.order.inProgress,
        loading: this.order && this.order.inProgress,
        hidden: this.order && !this.order.id
      }
    },

    hasShippingMethod: {
      cache: false,
      get: function () {
        return !!this.order.shipping.method
      }
    },

    couponSelectPlaceholder () {
      return this.coupons.length === 0 ? this.$t('No coupons available') : this.$t('Please select the coupon')
    }
  },

  async mounted () {
    if (this.$route.params.id) {
      await this.load(this.$route.params.id)
    }

    if (this.$route.params.customer) {
      const fetched = await this.$http.customer.find(this.$route.params.customer)

      if (fetched.addresses.length) {
        const defaultAddress = fetched.addresses.find(address => address.is_default)

        if (defaultAddress) {
          this.order.shipping_address = defaultAddress
          this.order.billing_address = defaultAddress
        } else {
          this.order.shipping_address = {}
          this.order.billing_address = {}
        }
      }

      this.order.customer = {
        ...fetched,
        customer_id: fetched.id
      }
    }

    if (!this.order.customer.customer_id) {
      await this.changeCustomer()
    }

    if (!this.order.id && !this.order.customer.customer_id && !this.order.items.length) {
      this.goBack()
    }

    const coupons = await this.$http.make('campaign').where('type', 'eq', 'coupon').get()
    this.coupons = coupons.map(coupon => coupon.code)
  },

  methods: {
    async load (id) {
      this.order = await this.$http.cart.find(id)
      this.order.shipping = this.order.shipping || {}

      // TODO This is a temporary fix for backend cart model missing customer's dob
      if (this.order.customer.customer_id && !this.order.customer.dob) {
        const fetched = await this.$http.customer.find(this.order.customer.customer_id)
        this.order.customer.dob = fetched.dob
      }
    },

    goBack () {
      this.$router.push({ name: 'quotes' })
    },

    onOpenProductSearch () {
      this.searchDialog = true
    },

    onProductSelected (product) {
      this.selectedProduct = product
      this.productDialog = true
      this.searchDialog = false
    },

    onProductCancelled () {
      this.selectedProduct = null
      this.productDialog = false
    },

    async onAddProduct (product) {
      this.productDialog = false

      let productDto = {
        ...product,
        product_id: product.product_id,
        quantity: product.quantity,
        notes: product.notes
      }

      if (product.components && product.components.length) {
        productDto.components = product.components
      }

      try {
        await this.order.syncItems(productDto)
        this.$toast.success(this.$t('Item has been added to cart'))
      } catch (err) {
        this.$toast.error(this.$t('Error occurred while adding the item. Please try again'))
      }
    },

    async onRemoveProduct (id) {
      try {
        await this.order.removeItem(id)
        this.$toast.success(this.$t('Item has been removed from cart'))
      } catch (err) {
        this.$toast.error(this.$t('Error occurred while removing the item. Please try again'))
      }
    },

    applyCoupon () {
      this.order.applyCoupon(this.selectedCoupon)
      this.editCoupon = false
    },

    removeCoupon () {
      this.order.removeCoupon()
      this.selectedCoupon = null
    },

    async onCreate () {
      this.isCreating = true

      let shippingAddress
      let billingAddress
      let shipping

      if (this.order.shipping_address) {
        shippingAddress = this.order.shipping_address.id
        delete this.order._attributes.shipping_address
      }

      if (this.order.billing_address) {
        billingAddress = this.order.billing_address.id
        delete this.order._attributes.billing_address
      }

      if (this.order.shipping.method) {
        shipping = this.order.shipping.method
      }

      delete this.order._attributes.shipping

      await this.order.save()

      if (shippingAddress) {
        await this.order.setShippingAddress(shippingAddress)
      }

      if (billingAddress) {
        await this.order.setBillingAddress(billingAddress)
      }

      if (shipping) {
        await this.order.setShippingMethod(shipping)
      }

      if (this.order.note) {
        await this.order.addNote(this.order.note)
      }

      this.$router.push({
        name: 'draft-order-saved',
        params: { id: this.order.id }
      })

      this.isCreating = false

      this.$toast.info(this.$t('Draft Order Saved'))
    },

    async onActionCheckout () {
      this.isCheckingOut = true
      await this.order.checkout()
      this.isCheckingOut = false
      this.redirect()
    },

    redirect () {
      this.$http.make('order').find(this.order.id).then(() => {
        this.redirectAttempts = 0
        this.$router.push({
          name: 'single-order',
          params: {
            id: this.order.id
          }
        })
      }).catch(e => {
        if (e.response.status === 404 && this.redirectAttempts < 3) {
          setTimeout(() => {
            this.redirectAttempts++
            this.redirect()
          }, 1000)
        }

        if (this.redirectAttempts >= 3) {
          this.$toast.error(this.$t('Cannot find order created from this Cart. Redirecting to Orders list.'))
          this.redirectAttempts = 0
          this.$router.push('/orders')
        }
      })
    },

    async onActionCancel () {
      this.goBack()
    },

    async onActionDelete () {
      await this.order.delete()
      this.goBack()
    },

    async changeCustomer () {
      const customer = await this.$refs.search_customer.find()
      if (!customer) {
        return
      }
      const fetched = await this.$http.customer.find(customer.id)

      this.order.customer = {
        ...customer.payload(),
        customer_id: customer.id
      }

      if (fetched.addresses.length) {
        const defaultAddress = fetched.addresses.find(address => address.is_default)

        if (defaultAddress) {
          this.order.shipping_address = defaultAddress
          this.order.billing_address = defaultAddress
        } else {
          this.order.shipping_address = {}
          this.order.billing_address = {}
        }
      }

      await this.onCreate()
    },

    async changeShippingMethod (shipping) {
      if (!shipping) {
        return
      }
      if (this.order.id) {
        await this.order.setShippingMethod(shipping)
      }

      this.order.shipping.method = shipping
    },

    async changeBillingAddress () {
      const address = await this.$refs.search_address.find(this.order.customer.customer_id)
      if (!address) {
        return
      }
      if (this.order.id) {
        await this.order.setBillingAddress(address.id)
      }

      this.order.billing_address = address
    },

    async changeShippingAddress () {
      const address = await this.$refs.search_address.find(this.order.customer.customer_id)
      if (!address) {
        return
      }
      if (this.order.id) {
        await this.order.setShippingAddress(address.id)
      }

      this.order.shipping_address = address
    },

    async updateItem (id) {
      const item = this.order.items.find(i => i.id === id)
      await this.order.updateItem(item.id, {
        quantity: item.quantity
      })
    }
  }
}
</script>

<style>
  .search-field .v-text-field__details {
    display: none;
  }

  .search-field .v-input__slot {
    margin-bottom: 0;
  }
</style>
